import { useStoreLogger } from '../logger.ts'
import {
  WishListCreate,
  WishListDelete,
  WishListGetList,
  WishlistSkuGetList,
  WishlistSkuCreate,
  WishlistSkuDelete,
} from '../api/WishList.ts'

import type {
  IAoeAllWishList,
  IAoeAllWishListName,
  IAoeAllWishListSkus,
  IAoeWishListDetails,
  IAoeWishListAddResponse
} from '../types/WishListTypes.ts'

import {
  GetBeeNumberAuthenicatedUser,
  GetOrderTypeId,
} from '../stores/SettingsStore.ts'

import { Mutex } from 'async-mutex'

export async function SaveWishlistToLocalStorage(wishlist: IAoeAllWishList) {
  const logger = useStoreLogger()
  logger.debug('SaveWishlistToLocalStorage')
  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser) {
    wishlist.BeeNumber = beeNumberAuthenicatedUser
    const newValue = JSON.stringify(wishlist)
    const key = 'AoeAllWishList'
    localStorage.setItem(key, newValue)
    window.dispatchEvent(
      new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
    )
  }
}

const GetWishListFromLocalStorageMutex = new Mutex()

export async function GetWishListFromLocalStorage(): Promise<IAoeAllWishList> {
  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser) {
    let allWishLists = GetWishListFromLocalStorageCommon(
      beeNumberAuthenicatedUser,
    )
    if (allWishLists) return allWishLists

    const release = await GetWishListFromLocalStorageMutex.acquire()
    try {
      allWishLists = GetWishListFromLocalStorageCommon(
        beeNumberAuthenicatedUser,
      )
      if (allWishLists) return allWishLists
      allWishLists = await UpdateWishListLocalStorage()
      return allWishLists
    } finally {
      release()
    }
  }
  return {
    wishListNames: [] as IAoeAllWishListName[],
    skus: [] as IAoeAllWishListSkus[],
  } as IAoeAllWishList
}

function RemoveWishListFromLocalStorage() {
  const key = 'AoeAllWishList'
  localStorage.removeItem(key)
}

function GetWishListFromLocalStorageCommon(
  beeNumberAuthenicatedUser: number,
): IAoeAllWishList | null {
  const key = 'AoeAllWishList'
  const json = localStorage.getItem(key)
  if (json) {
    const allWishLists = JSON.parse(json) as IAoeAllWishList
    if (allWishLists.BeeNumber === beeNumberAuthenicatedUser) {
      return allWishLists
    }
  }

  return null
}

async function UpdateWishListLocalStorage(): Promise<IAoeAllWishList> {
  const logger = useStoreLogger()
  logger.debug('UpdateWishListLocalStorage')
  const orderTypeId = await GetOrderTypeId()
  const wishLists = await WishListGetList()
    const allWishLists = {} as IAoeAllWishList
    allWishLists.wishListNames = [] as IAoeAllWishListName[]
    allWishLists.skus = [] as IAoeAllWishListSkus[]
    const skuList = [] as string[]
    logger.debug('wishLists:', wishLists)
    for (let i = 0; i < wishLists.items.length; i++) {
      const wName = {
        aoeWishlistId: wishLists.items[i].aoeWishlistId,
        wishListName: wishLists.items[i].wishListName,
      }
      allWishLists.wishListNames.push(wName)
      const wishListDetail = await GetWishListDetail(
        wishLists.items[i].wishListName,
        orderTypeId,
      )
      for (let w = 0; w < wishListDetail.items.length; w++) {
        if (skuList.includes(wishListDetail.items[w].skuCode)) {
          for (let j = 0; j < allWishLists.skus.length; j++) {
            if (allWishLists.skus[j].skuCode == wishListDetail.items[w].skuCode) {
              allWishLists.skus[j].wishListNames.push(
                wishListDetail.items[i].wishListName,
              )
            }
          }
        } else {
          const wNames = [] as string[]
          wNames.push(wishListDetail.items[w].wishListName)
          const wSku = {
            aoeWishlistId: wishLists.items[i].aoeWishlistId,
            wishListNames: wNames,
            skuCode: wishListDetail.items[w].skuCode,
            productCode: wishListDetail.items[w].productCode,
          }
          allWishLists.skus.push(wSku)
          skuList.push(wishListDetail.items[w].skuCode)
        }
      }
    }
    await SaveWishlistToLocalStorage(allWishLists)
    return allWishLists
  }

export async function GetWishListItems(): Promise<IAoeAllWishList> {
  const wl = await GetWishListFromLocalStorage()
  return wl
}

export async function AddWishList(wishListName: string) : Promise<IAoeWishListAddResponse> {
  const logger = useStoreLogger()
  logger.debug('addWishList wishListName:', wishListName)
  const resp = await WishListCreate(wishListName)
  RemoveWishListFromLocalStorage()
  return { aoeWishlistId: resp.aoeWishlistId }
}

export async function DeleteWishList(wishListName: string) {
  const logger = useStoreLogger()
  logger.debug('deleteWishList wishListName:', wishListName)

  WishListDelete(wishListName).then(async () => {
    await UpdateWishListLocalStorage()
  })
}


export async function AddItemToWishList(
  wishListName: string | null,
  skuCode: string,
) {
  const logger = useStoreLogger()
  logger.debug(
    'addItemToWishList wishListName:',
    wishListName,
    'skuCode:',
    skuCode,
  )

  await WishlistSkuCreate(wishListName, skuCode)
  await UpdateWishListLocalStorage()
}

export async function RemoveItemFromWishLists(skuCode: string) {
  const logger = useStoreLogger()
  logger.debug('RemoveItemFromWishLists')

  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser) {
    GetWishListFromLocalStorage().then(lists => {
      for (let i = 0; i < lists.skus.length; i++) {
        if (lists.skus[i].skuCode == skuCode) {
          for (let j = 0; j < lists.skus[i].wishListNames.length; j++) {
            WishlistSkuDelete(lists.skus[i].wishListNames[j], skuCode).then(
              async () => {
                await UpdateWishListLocalStorage()
              },
            )
          }
        }
      }
    })
  }
}

export async function RemoveItemFromWishList(skuCode: string, wishlistName: string) {
  const logger = useStoreLogger()
  logger.debug('RemoveItemFromWishList')

  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuthenicatedUser) {
    WishlistSkuDelete(wishlistName, skuCode).then(
      async () => {
        await UpdateWishListLocalStorage()
      },
    )
  }
}

export async function GetWishListDetail(
  wishListName: string,
  ordertypeId: string,
) {
  const logger = useStoreLogger()
  logger.debug('getWishLists')

  const getRequest = await WishlistSkuGetList(wishListName, ordertypeId)
  logger.debug('getRequest:', getRequest)
  const wishListDetail = {} as IAoeWishListDetails
  wishListDetail.items = []
  for (let i = 0; i < getRequest.items.length; i++) {
    const wishListsku = {
      aoeWishlistSkuId: getRequest.items[i].aoeWishlistSkuId,
      wishListName: getRequest.items[i].wishListName,
      productName: getRequest.items[i].sku.product.productName,
      price: getRequest.items[i].sku.retailPrice,
      skuCode: getRequest.items[i].sku.skuCode,
      categoryCode: getRequest.items[i].sku.categoryCode,
      productCode: getRequest.items[i].sku.product.productCode,
      image: getRequest.items[i].sku.product.attributes.CatalogImage ?? '',
      inventoryLevel: getRequest.items[i].sku.inventoryLevel,
    }
    wishListDetail.items.push(wishListsku)
  }

  return wishListDetail
}
